/**
 * Simple Visualizer
 *
 * A lightweight CSS-based audio visualizer for mobile/low-power devices.
 * Uses CSS animations instead of canvas for better performance.
 */

import React, { useEffect, useState, useRef } from 'react';

interface SimpleVisualizerProps {
  analyser: AnalyserNode | null;
  inputLevel?: number;
  isConnected: boolean;
  isConnecting?: boolean;
}

const USER_INPUT_THRESHOLD = 0.01;

const SimpleVisualizer: React.FC<SimpleVisualizerProps> = ({
  analyser,
  inputLevel = 0,
  isConnected,
  isConnecting = false,
}) => {
  const [audioLevel, setAudioLevel] = useState(0);
  const animationRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const loopTokenRef = useRef(0);
  const isMobile =
    (typeof window !== 'undefined' && 'ontouchstart' in window) ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);

  // Sample audio level at 10fps (every 100ms) for performance
  useEffect(() => {
    if (!analyser || !isConnected) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      setAudioLevel(0);
      return;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const UPDATE_INTERVAL = isMobile ? 120 : 100;
    const loopToken = ++loopTokenRef.current;
    lastUpdateRef.current = 0;

    const updateLevel = (timestamp: number) => {
      if (loopTokenRef.current !== loopToken) {
        return;
      }

      if (timestamp - lastUpdateRef.current >= UPDATE_INTERVAL) {
        try {
          analyser.getByteFrequencyData(dataArray);
          const sum = dataArray.reduce((a, b) => a + b, 0);
          const average = sum / dataArray.length / 255;
          setAudioLevel(average);
        } catch {
          setAudioLevel(0);
        }
        lastUpdateRef.current = timestamp;
      }
      animationRef.current = requestAnimationFrame(updateLevel);
    };

    animationRef.current = requestAnimationFrame(updateLevel);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [analyser, isConnected, isMobile]);

  // Determine visual state
  const getStateClass = () => {
    if (isConnecting) return 'connecting';
    if (!isConnected) return 'idle';
    if (inputLevel > USER_INPUT_THRESHOLD) return 'user-speaking';
    if (audioLevel > 0.02) return 'speaking';
    return 'ready';
  };

  // Scale pulse based on audio level (1.0 to 1.3)
  const pulseScale = 1 + (Math.max(audioLevel, inputLevel) * 0.35);

  return (
    <div
      className={`simple-visualizer ${getStateClass()}`}
      data-mobile={isMobile ? 'true' : 'false'}
      style={{ '--pulse-scale': pulseScale } as React.CSSProperties}
    >
      <div className="simple-ring outer" />
      <div className="simple-ring inner" />
    </div>
  );
};

export default SimpleVisualizer;
